import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  GraduationCap, Calculator, ShieldCheck, ArrowRight, Clock,
  CheckCircle2, University, FileCheck2, Lock, Star, BookOpen,
} from "lucide-react";
import { Link } from "react-router-dom";
import { SiteFooter } from "@/components/PublicLayout";
import { ARTICLES } from "@/content/articles";

const steps = [
  { icon: Calculator, title: "Enter Your Marks", desc: "Add your subjects & percentages. We calculate your APS instantly — excluding Life Orientation." },
  { icon: GraduationCap, title: "See What You Qualify For", desc: "Our engine matches you to real university courses based on their admission requirements." },
  { icon: Lock, title: "Unlock Your Matches — R26.99", desc: "A once-off, non-refundable fee reveals every course you qualify for." },
  { icon: FileCheck2, title: "Apply — R77.99 per University", desc: "Complete your application through us. If your application is unsuccessful you're refunded 50% — refunds are processed within 3–12 days." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [accepted, setAccepted] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/settings/stats`)
      .then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const totalStudents = stats?.students || 0;
  const studentLabel = totalStudents > 0
    ? `${totalStudents.toLocaleString()}+ student${totalStudents === 1 ? "" : "s"}`
    : "thousands of students";

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />

      {/* Hero — cover */}
      <section className="relative overflow-hidden grain">
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-0">
          <div className="absolute -top-32 -left-24 w-[26rem] h-[26rem] rounded-full bg-[#F5C32C]/20 blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[30rem] h-[30rem] rounded-full bg-[#0B2447]/10 blur-3xl" />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 pt-10 pb-16 md:pt-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white border border-slate-200 rounded-[2rem] shadow-xl px-6 py-9 sm:px-10 md:px-12 md:py-12 text-center relative overflow-hidden"
          >
            <div aria-hidden className="pointer-events-none absolute -left-10 top-1/3 w-72 h-40 opacity-50"
              style={{ background: "radial-gradient(140px 44px at 30% 50%, rgba(245,195,44,0.35), transparent 70%)" }} />

            {/* Brand */}
            <div className="flex items-center justify-center gap-3">
              <div className="relative w-12 h-12 rounded-xl bg-[#0B2447] flex items-center justify-center shrink-0">
                <GraduationCap className="w-7 h-7 text-white" />
                <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#F5C32C]" />
              </div>
              <div className="text-left">
                <div className="font-display text-2xl sm:text-3xl md:text-4xl font-bold leading-none">
                  <span className="text-[#0B2447]">Your Varsity </span><span className="text-[#F5C32C]">Apply</span>
                </div>
                <div className="text-slate-500 text-sm mt-1">We apply. You qualify.</div>
              </div>
            </div>

            {/* Social proof — live student count + rating */}
            <div className="mt-8 mx-auto max-w-sm rounded-2xl border border-[#F5C32C]/30 bg-gradient-to-br from-[#0B2447] to-[#1b3a63] px-6 py-5 shadow-lg text-white" data-testid="social-proof-card">
              <div className="flex items-center justify-center gap-1.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-[#F5C32C] text-[#F5C32C]" />
                ))}
                <span className="ml-1.5 font-bold text-[#F5C32C]" data-testid="rating-value">{(stats?.rating ?? 4.9).toFixed(1)}</span>
              </div>
              <p className="mt-3 text-sm text-slate-200 leading-snug text-center">
                <span className="font-bold text-white" data-testid="student-count">Join {studentLabel}</span> who&rsquo;ve already signed up and applied with us.
              </p>
            </div>

            <div className="my-8 h-px bg-slate-200" />

            {/* Headline */}
            <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0B2447]">Your Future. Our Mission.</h1>
            <p className="mt-3 text-lg md:text-xl text-slate-600">University Applications,<br /><span className="text-[#F5C32C] font-bold">Made Easy</span></p>

            {/* Get started */}
            <button
              data-testid="hero-get-started-btn"
              disabled={!accepted}
              onClick={() => navigate("/register")}
              className="mt-8 w-full max-w-md mx-auto flex items-center justify-center relative bg-[#0B2447] hover:bg-[#153461] disabled:opacity-50 text-white font-bold text-lg rounded-2xl px-7 py-4 transition-colors"
            >
              <span>Get started</span>
              <ArrowRight className="w-6 h-6 text-[#F5C32C] absolute right-6" />
            </button>

            {/* Accept */}
            <label className="mt-5 flex items-center justify-center gap-3 cursor-pointer max-w-md mx-auto">
              <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} data-testid="accept-rules-checkbox" />
              <span className="text-sm text-slate-600 text-left">I agree to the <Link to="/terms" className="font-bold text-[#0B2447]">Terms &amp; Conditions</Link> and <Link to="/privacy" className="font-bold text-[#0B2447]">Privacy Policy</Link></span>
            </label>
            {!accepted && <p className="mt-1 text-xs text-slate-400">Tick the box to get started.</p>}

            <div className="mt-6">
              <Link to="/login" data-testid="hero-login-btn" className="text-[#0B2447] font-semibold underline underline-offset-4">Already have an account? Sign In</Link>
            </div>

            <div className="my-7 h-px bg-slate-200" />

            {/* Trust icons */}
            <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
              {[{ Icon: ShieldCheck, t: "Secure & Reliable" }, { Icon: Clock, t: "Apply in Minutes" }, { Icon: GraduationCap, t: "All Universities" }].map(({ Icon, t }) => (
                <div key={t} className="flex flex-col items-center gap-2">
                  <Icon className="w-8 h-8 text-[#0B2447]" />
                  <span className="text-xs font-semibold text-[#0B2447] text-center leading-tight">{t}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* bottom navy band */}
          <div className="mt-6 rounded-[1.5rem] bg-[#0B2447] px-6 py-5 text-center relative overflow-hidden">
            <div aria-hidden className="absolute inset-x-0 top-0 h-1.5 bg-[#F5C32C]/80" />
            <div className="flex items-center justify-center gap-2 text-white">
              <ShieldCheck className="w-5 h-5 text-[#F5C32C] shrink-0" />
              <span>We apply to all universities for you</span>
            </div>
            <p className="mt-1 text-[#F5C32C] font-semibold">Fast. Easy. Reliable.</p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">How it works</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#0B2447]">
            Four simple steps to your university placement.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-200"
              >
                <div className="w-12 h-12 rounded-lg bg-[#0B2447]/5 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-[#0B2447]" />
                </div>
                <div className="text-xs font-bold text-[#F5C32C]">STEP {i + 1}</div>
                <h3 className="font-display font-semibold text-lg text-[#0B2447] mt-1">{s.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Highlights — moved here from hero to save space */}
      <section className="max-w-7xl mx-auto px-6 pb-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-[#0B2447] rounded-2xl shadow-2xl p-8 md:p-10 text-white overflow-hidden relative"
          data-testid="landing-highlights"
        >
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#F5C32C]/10" />
          <div className="absolute -right-4 top-16 w-24 h-24 rounded-full bg-[#F5C32C]/10" />
          <div className="relative flex items-center gap-3">
            <University className="w-9 h-9 text-[#F5C32C]" />
            <h3 className="font-display text-2xl font-bold leading-tight">
              Everything you need to secure your place.
            </h3>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6 relative">
            <div>
              <div className="font-display font-bold text-4xl text-[#F5C32C]">55+</div>
              <div className="text-xs text-white/60 uppercase tracking-wider mt-1">TUT courses matched</div>
            </div>
            <div>
              <div className="font-display font-bold text-3xl text-[#F5C32C]">R26.99</div>
              <div className="text-xs text-white/60 uppercase tracking-wider mt-1">Once-off to unlock</div>
            </div>
            <div>
              <div className="font-display font-bold text-4xl text-[#F5C32C]">APS</div>
              <div className="text-xs text-white/60 uppercase tracking-wider mt-1">Auto-calculated</div>
            </div>
            <div>
              <div className="font-display font-bold text-3xl text-[#F5C32C]">R77.99</div>
              <div className="text-xs text-white/60 uppercase tracking-wider mt-1">Per application</div>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-2 text-sm text-white/70 relative">
            <ShieldCheck className="w-4 h-4 text-[#F5C32C]" /> Secured by Paystack
          </div>
        </motion.div>
      </section>

      {/* Trust */}
      <section className="bg-[#0B2447] text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-8">
          {[
            { icon: ShieldCheck, title: "Secure Payments", desc: "All payments processed securely via Paystack. Your data is protected." },
            { icon: CheckCircle2, title: "Real Requirements", desc: "Matches use official university APS & subject requirements — starting with TUT." },
            { icon: FileCheck2, title: "Refund Protection", desc: "The R77.99 application fee is refundable if your application is not submitted — refunds are processed within 3–12 days." },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.title} className="flex gap-4">
                <Icon className="w-8 h-8 text-[#F5C32C] shrink-0" />
                <div>
                  <h3 className="font-display font-semibold text-lg">{t.title}</h3>
                  <p className="text-sm text-white/70 mt-1 leading-relaxed">{t.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-14" data-testid="landing-guides">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2"><BookOpen className="w-4 h-4" /> Free application guides</p>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0B2447] mt-2">Learn before you apply</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {ARTICLES.slice(0, 6).map((a) => (
            <Link key={a.slug} to={`/resources/${a.slug}`} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow" data-testid={`landing-guide-${a.slug}`}>
              <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#B8860B]">{a.category}</span>
              <p className="font-display font-bold text-[#0B2447] mt-1.5">{a.title}</p>
            </Link>
          ))}
        </div>
        <Link to="/resources" className="inline-flex items-center gap-1.5 mt-6 text-sm font-semibold text-[#0B2447]" data-testid="landing-all-guides">All guides <ArrowRight className="w-4 h-4" /></Link>
      </section>

      <SiteFooter />
    </div>
  );
}

